import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { askGPT } from './OpenAIService';
import { sendEmail } from './EmailService';
import sourceService from '../CP-Sourcing/SourceService';
import synthesisService from '../CP-Search/SynthesisService';
import { 
  Loader2, 
  Send, 
  AlertTriangle, 
  Sparkles, 
  FileText,
  Lightbulb,
  BookOpen,
  Target,
  List,
  Globe,
  Check,
  Eye,
  EyeOff,
  ChevronRight,
  Info,
  Plus,
  X
} from 'lucide-react';

// =============================================
// FONCTIONS DE FORMATAGE DU CONTENU
// =============================================

/**
 * Nettoie et formate les recommandations
 */
const formatRecommendations = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Supprimer les marqueurs [SECTION1], [SECTION2], etc.
    .replace(/\[SECTION\d+\]/g, '')
    // Nettoyer les astérisques de formatage markdown
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Séparer les paragraphes par des sauts de ligne doubles
    .replace(/\n\n/g, '</p><p>')
    // Ajouter les balises de paragraphe
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
    // Nettoyer les espaces multiples
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Formate la synthèse avec structure claire
 */
const formatSynthesis = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Détecter et formater les titres numérotés (1., 2., etc.)
    .replace(/(\d+\.\s*\*\*)(.*?)(\*\*)/g, '<h4 style="color: #1F2937; font-weight: 600; margin: 20px 0 10px 0; font-size: 16px;">🔹 $2</h4>')
    // Formater les sous-titres avec **
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #374151;">$1</strong>')
    // Formater les liens [texte](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #4F46E5; text-decoration: none; font-weight: 500;">$1</a>')
    // Séparer les paragraphes
    .replace(/\n\n/g, '</p><p style="margin: 15px 0; line-height: 1.6;">')
    // Ajouter les balises de paragraphe
    .replace(/^/, '<p style="margin: 15px 0; line-height: 1.6;">')
    .replace(/$/, '</p>')
    // Nettoyer les espaces multiples
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Formate le plan d'action JSON en HTML structuré
 */
const formatActionPlan = (actionPlan) => {
  if (!actionPlan) return '';
  
  let planData;
  
  // Parser le JSON si c'est une chaîne
  if (typeof actionPlan === 'string') {
    try {
      planData = JSON.parse(actionPlan);
    } catch (e) {
      // Si ce n'est pas du JSON valide, retourner le texte formaté
      return formatSynthesis(actionPlan);
    }
  } else if (typeof actionPlan === 'object') {
    planData = actionPlan;
  } else {
    return '';
  }
  
  let html = '';
  
  // Titre du plan
  if (planData.titre) {
    html += `<h4 style="color: #1F2937; font-weight: 600; margin: 0 0 15px 0; font-size: 16px;">${planData.titre}</h4>`;
  }
  
  // Description
  if (planData.description) {
    html += `<p style="margin: 0 0 20px 0; line-height: 1.6; color: #4B5563; font-style: italic;">${planData.description}</p>`;
  }
  
  // Étapes
  if (planData.steps && Array.isArray(planData.steps)) {
    html += '<div style="margin: 20px 0;">';
    
    planData.steps.forEach((step, index) => {
      const stepNumber = step.id || (index + 1);
      const isCompleted = step.termine || false;
      const statusIcon = isCompleted ? '✅' : '📋';
      const statusColor = isCompleted ? '#10B981' : '#6B7280';
      
      html += `
        <div style="background: #F9FAFB; border-left: 4px solid ${isCompleted ? '#10B981' : '#4F46E5'}; margin: 15px 0; padding: 16px; border-radius: 6px;">
          <div style="display: flex; align-items: start; gap: 10px; margin-bottom: 8px;">
            <span style="font-size: 16px;">${statusIcon}</span>
            <h5 style="margin: 0; color: #1F2937; font-weight: 600; font-size: 15px;">
              Étape ${stepNumber} : ${step.titre || 'Sans titre'}
            </h5>
          </div>
          
          ${step.duree ? `<div style="margin: 8px 0; font-size: 13px; color: ${statusColor}; font-weight: 500;">
            ⏱️ Durée : ${step.duree}
          </div>` : ''}
          
          <p style="margin: 8px 0 0 0; line-height: 1.5; color: #374151; font-size: 14px;">
            ${step.description || 'Aucune description'}
          </p>
        </div>
      `;
    });
    
    html += '</div>';
  }
  
  return html;
};

/**
 * Formate les concepts (cours)
 */
const formatConcepts = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Formater les mots en gras
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #059669;">$1</strong>')
    // Séparer les paragraphes
    .replace(/\n\n/g, '</p><p style="margin: 15px 0; line-height: 1.6;">')
    // Ajouter les balises de paragraphe
    .replace(/^/, '<p style="margin: 15px 0; line-height: 1.6;">')
    .replace(/$/, '</p>')
    // Nettoyer
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Nettoie le texte en supprimant les marqueurs markdown pour l'aperçu
 */
const cleanTextForPreview = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .replace(/\[SECTION\d+\]/g, '')
    .replace(/\*\*/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
};

// =============================================
// SERVICE DE RÉCUPÉRATION DES DONNÉES
// =============================================

/**
 * Service amélioré pour récupérer les données complètes de l'objectif
 */
const fetchObjectiveData = async (userId, competenceId) => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }

    // 1. Récupération des données de base de la compétence
    const competenceDoc = await getDoc(doc(db, 'users', userId, 'competences', competenceId));
    
    if (!competenceDoc.exists()) {
      throw new Error('Objectif non trouvé');
    }

    const baseData = competenceDoc.data();
    
    // 2. Récupération des sources sauvegardées
    let formattedSources = [];
    try {
      const token = await user.getIdToken(true);
      
      const sourcesResult = await sourceService.getSavedSources(
        userId, 
        token, 
        { competenceId }
      );
      
      if (sourcesResult && sourcesResult.success && Array.isArray(sourcesResult.sources) && sourcesResult.sources.length > 0) {
        // Trier pour avoir la session la plus récente
        const sortedSessions = [...sourcesResult.sources].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.updatedAt || 0);
          const dateB = new Date(b.createdAt || b.updatedAt || 0);
          return dateB - dateA;
        });
        
        const latestSession = sortedSessions[0];
        
        if (latestSession.sources && Array.isArray(latestSession.sources)) {
          formattedSources = latestSession.sources.map(source => ({
            ...source,
            sessionId: latestSession.id,
            title: source.title || source.domain || 'Source sans titre',
            description: source.description || 'Aucune description disponible',
            url: source.url || `https://${source.domain}` || '#',
            domain: source.domain || 'Source externe',
            type: source.type || 'webpage',
            relevanceScore: source.relevanceScore || 5
          }));
        }
      }
    } catch (sourcesError) {
      console.warn('Erreur lors de la récupération des sources:', sourcesError);
    }

    // 3. Récupération de la synthèse
    let synthesisContent = '';
    try {
      const synthesisResult = await synthesisService.getLatestSynthesis(userId, competenceId);
      
      if (synthesisResult.success && synthesisResult.synthesis) {
        synthesisContent = synthesisResult.synthesis.content || '';
      }
    } catch (synthesisError) {
      console.warn('Erreur lors de la récupération de la synthèse:', synthesisError);
    }
    
    return {
      title: baseData.title || '',
      description: baseData.description || '',
      recommendation: baseData.recommendation || '',
      cours: baseData.cours || '',
      synthesis: synthesisContent || baseData.synthesis || '',
      sources: formattedSources.length > 0 ? formattedSources : baseData.sources || [],
      actionPlan: baseData.actionPlan || '',
      theme: baseData.theme || 'Personnel',
      type: baseData.type || '',
      createdAt: baseData.createdAt || new Date().toISOString(),
      updatedAt: baseData.updatedAt || new Date().toISOString()
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error);
    throw error;
  }
};

// =============================================
// HOOK POUR LA GÉNÉRATION DE MESSAGES IA
// =============================================

const useAIMessage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateMessage = useCallback(async (competence, selectedContent) => {
    if (!competence) {
      setError('Compétence non définie');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const contentTypes = {
        objective: 'l\'objectif principal',
        recommendations: 'les recommandations personnalisées',
        concepts: 'les concepts clés',
        synthesis: 'la synthèse de recherche',
        actionPlan: 'le plan d\'action détaillé',
        sources: 'les sources et références'
      };

      const selectedElements = selectedContent
        .filter(item => item.selected)
        .map(item => contentTypes[item.type])
        .join(', ');

      const prompt = `
        Tu es un assistant pédagogique de la plateforme Aladinn.
        Génère un message d'introduction chaleureux pour un email de partage d'objectif : "${competence.title}".

        Éléments inclus dans ce partage : ${selectedElements}

        Le message doit :
        1. Être chaleureux et personnel
        2. Expliquer brièvement l'objectif et pourquoi c'est intéressant
        3. Mentionner les éléments partagés de manière naturelle
        4. Évoquer l'aspect collaboratif et d'entraide
        5. Inclure des émojis appropriés
        6. Faire 100-150 mots maximum
        7. Se terminer par une invitation à découvrir Aladinn

        Thème de l'objectif : ${competence.theme || 'Personnel'}
        
        Ton : amical, motivant, sans être trop commercial
      `;

      const response = await askGPT(prompt);
      if (!response) throw new Error('Erreur lors de la génération du message');
      return response;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { generateMessage, loading, error };
};

// =============================================
// GÉNÉRATION D'EMAIL HTML
// =============================================

const generateExperienceShareHTML = (customMessage, selectedItems, contentTypes, objectiveData) => {
  let htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${objectiveData.title} - Partage d'expérience</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; 
            line-height: 1.7; 
            color: #2D3748; 
            background-color: #F7FAFC;
            padding: 20px;
        }
        .container { 
            max-width: 700px; 
            margin: 0 auto; 
            background: white; 
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }
        
        .main-content {
            padding: 40px;
            background: white;
        }
        
        .project-title {
            font-size: 32px;
            font-weight: 700;
            color: #1F2937;
            margin-bottom: 16px;
            line-height: 1.2;
            text-align: center;
        }
        
        .project-meta {
            display: flex;
            justify-content: center;
            gap: 12px;
            margin-bottom: 30px;
            flex-wrap: wrap;
        }
        .meta-badge {
            padding: 8px 16px;
            border-radius: 25px;
            font-size: 13px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .type-badge { 
            background: linear-gradient(135deg, #EEF2FF, #E0E7FF); 
            color: #3730A3; 
            border: 1px solid #C7D2FE;
        }
        .theme-badge { 
            background: linear-gradient(135deg, #F0FDF4, #DCFCE7); 
            color: #166534; 
            border: 1px solid #BBF7D0;
        }
        
        .intro-message {
            font-size: 18px;
            line-height: 1.8;
            color: #374151;
            margin-bottom: 35px;
            text-align: left;
            background: linear-gradient(135deg, #F8FAFC, #EDF2F7);
            padding: 25px;
            border-radius: 10px;
            border-left: 4px solid #4F46E5;
        }
        
        .project-description {
            font-size: 17px;
            color: #4B5563;
            line-height: 1.7;
            margin-bottom: 40px;
            text-align: center;
            font-style: italic;
        }
        
        .content-section {
            margin: 30px 0;
            background: transparent;
        }
        .content-title {
            font-size: 20px;
            font-weight: 600;
            color: #1F2937;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 10px;
            padding-bottom: 8px;
            border-bottom: 2px solid #E5E7EB;
        }
        .content-text {
            font-size: 16px;
            line-height: 1.8;
            color: #374151;
            margin-bottom: 25px;
        }
        
        .recommendations-section .content-title { color: #7C3AED; border-bottom-color: #7C3AED; }
        .concepts-section .content-title { color: #059669; border-bottom-color: #059669; }
        .synthesis-section .content-title { color: #DC2626; border-bottom-color: #DC2626; }
        .actionplan-section .content-title { color: #2563EB; border-bottom-color: #2563EB; }
        .sources-section .content-title { color: #EA580C; border-bottom-color: #EA580C; }
        
        .sources-list {
            margin: 20px 0;
        }
        .source-item {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border-left: 3px solid #4F46E5;
            margin-bottom: 12px;
        }
        .source-title { 
            font-weight: 600; 
            color: #1F2937; 
            margin-bottom: 6px; 
            font-size: 15px;
        }
        .source-url { 
            color: #4F46E5; 
            font-size: 14px; 
            word-break: break-all; 
            text-decoration: none;
            display: block;
            margin-bottom: 6px;
        }
        .source-url:hover { text-decoration: underline; }
        .source-description { 
            color: #6B7280; 
            font-size: 14px; 
            line-height: 1.5;
        }
        
        .content-text h4 {
            margin: 20px 0 10px 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .content-text p {
            margin: 15px 0;
            line-height: 1.6;
        }
        
        .content-text strong {
            font-weight: 600;
        }
        
        .content-text a {
            color: #4F46E5;
            text-decoration: none;
            font-weight: 500;
        }
        
        .content-text a:hover {
            text-decoration: underline;
        }
        
        .footer {
            background: #F8FAFC;
            padding: 30px;
            text-align: center;
            border-top: 1px solid #E5E7EB;
        }
        .footer-text {
            font-size: 14px;
            color: #6B7280;
            margin-bottom: 15px;
        }
        .cta-link {
            color: #4F46E5;
            text-decoration: none;
            font-weight: 600;
        }
        .cta-link:hover { text-decoration: underline; }
        
        @media (max-width: 600px) {
            .container { margin: 0 10px; }
            .main-content { padding: 25px 20px; }
            .project-title { font-size: 26px; }
            .intro-message { font-size: 16px; padding: 20px; }
            .content-text { font-size: 15px; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="main-content">
            <h1 class="project-title">${objectiveData.title}</h1>
            <div class="project-meta">
                ${objectiveData.type ? `<span class="meta-badge type-badge">${
                    objectiveData.type === 'project' ? '🚀 Projet' :
                    objectiveData.type === 'idea' ? '💡 Idée' :
                    objectiveData.type === 'desire' ? '✨ Ambition' :
                    objectiveData.type === 'skill' ? '🎯 Apprentissage' : 'Objectif'
                }</span>` : ''}
                ${objectiveData.theme ? `<span class="meta-badge theme-badge">📂 ${objectiveData.theme}</span>` : ''}
            </div>
            
            ${objectiveData.description ? `<p class="project-description">${objectiveData.description}</p>` : ''}
            
            <div class="intro-message">
                ${customMessage.replace(/\n/g, '<br>')}
            </div>`;

  selectedItems.forEach(item => {
    const contentType = contentTypes.find(ct => ct.type === item.type);
    if (contentType && objectiveData) {
      const content = contentType.getContent(objectiveData);
      if (content && typeof content === 'string' && content.trim()) {
        
        if (item.type === 'objective') return;
        
        const sectionConfig = {
          recommendations: { icon: '💡', class: 'recommendations-section', title: 'Mes recommandations' },
          concepts: { icon: '📚', class: 'concepts-section', title: 'Concepts clés découverts' },
          synthesis: { icon: '📄', class: 'synthesis-section', title: 'Synthèse de mes recherches' },
          actionPlan: { icon: '📋', class: 'actionplan-section', title: 'Plan d\'action' },
          sources: { icon: '🌐', class: 'sources-section', title: 'Sources utiles' }
        };

        const config = sectionConfig[item.type];
        if (!config) return;

        htmlContent += `
            <div class="content-section ${config.class}">
                <h2 class="content-title">
                    <span>${config.icon}</span>
                    ${config.title}
                </h2>`;

        if (item.type === 'sources') {
          const sources = objectiveData.sources;
          if (Array.isArray(sources) && sources.length > 0) {
            htmlContent += `<div class="sources-list">`;
            sources.forEach((source, index) => {
              htmlContent += `
                <div class="source-item">
                    <div class="source-title">${source.title || source.domain || `Ressource ${index + 1}`}</div>
                    ${source.url ? `<a href="${source.url}" class="source-url">${source.url}</a>` : ''}
                    ${source.description ? `<div class="source-description">${source.description}</div>` : ''}
                </div>`;
            });
            htmlContent += `</div>`;
          }
        } else {
          htmlContent += `<div class="content-text">${content}</div>`;
        }

        htmlContent += `</div>`;
      }
    }
  });

  htmlContent += `
        </div>
        
        <div class="footer">
            <div class="footer-text">
                Partagé avec <a href="https://aladinn-edu.web.app" class="cta-link">Aladinn</a> - Votre assistant d'apprentissage personnalisé
            </div>
        </div>
    </div>
</body>
</html>`;

  return htmlContent;
};

// =============================================
// COMPOSANTS UI
// =============================================

const SelectableContentItem = ({ 
  type, 
  title, 
  description, 
  icon: Icon, 
  selected, 
  onToggle, 
  hasContent,
  preview,
  color = "blue"
}) => {
  const colorClasses = {
    blue: { selected: 'border-blue-500 bg-blue-50', icon: 'bg-blue-100 text-blue-600' },
    purple: { selected: 'border-purple-500 bg-purple-50', icon: 'bg-purple-100 text-purple-600' },
    green: { selected: 'border-green-500 bg-green-50', icon: 'bg-green-100 text-green-600' },
    orange: { selected: 'border-orange-500 bg-orange-50', icon: 'bg-orange-100 text-orange-600' }
  };

  const currentColor = colorClasses[color] || colorClasses.blue;

  return (
    <div className={`border rounded-lg p-3 lg:p-4 transition-all duration-200 cursor-pointer ${
      selected ? currentColor.selected : hasContent ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm' : 'border-gray-100 bg-gray-50'
    }`} onClick={() => hasContent && onToggle(type)}>
      <div className="flex items-start gap-3">
        <div className={`p-1.5 lg:p-2 rounded-lg flex-shrink-0 ${selected ? currentColor.icon : hasContent ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'}`}>
          <Icon className="h-4 w-4 lg:h-5 lg:w-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h4 className={`font-medium text-sm lg:text-base ${hasContent ? 'text-gray-900' : 'text-gray-400'}`}>
                {title}
              </h4>
              <p className={`text-xs lg:text-sm mt-1 ${hasContent ? 'text-gray-600' : 'text-gray-400'}`}>
                {description}
              </p>
              {preview && hasContent && (
                <p className="text-xs text-gray-500 mt-2 line-clamp-2 lg:truncate">
                  {preview}
                </p>
              )}
            </div>
            
            <div className="flex-shrink-0">
              <div className={`w-4 h-4 lg:w-5 lg:h-5 rounded border-2 flex items-center justify-center ${
                selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
              }`}>
                {selected && <Check className="h-2.5 w-2.5 lg:h-3 lg:w-3 text-white" />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailInput = ({ selectedEmails, onEmailChange }) => {
  const [customEmail, setCustomEmail] = useState('');

  const handleAddCustom = () => {
    if (customEmail && customEmail.includes('@') && !selectedEmails.includes(customEmail)) {
      onEmailChange([...selectedEmails, customEmail]);
      setCustomEmail('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm lg:text-base font-medium text-gray-700">
          Destinataires ({selectedEmails.length})
        </label>
        {selectedEmails.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedEmails.map((email, index) => (
              <span key={index} className="inline-flex items-center gap-1 px-2 lg:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs lg:text-sm">
                <span className="truncate max-w-[150px] lg:max-w-none">{email}</span>
                <button
                  onClick={() => onEmailChange(selectedEmails.filter(e => e !== email))}
                  className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          placeholder="Ajouter une adresse email"
          value={customEmail}
          onChange={(e) => setCustomEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
        />
        <button
          onClick={handleAddCustom}
          disabled={!customEmail || !customEmail.includes('@') || selectedEmails.includes(customEmail)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm lg:text-base"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Ajouter</span>
        </button>
      </div>
    </div>
  );
};

// =============================================
// CONFIGURATION DES TYPES DE CONTENU
// =============================================

const contentTypes = [
  {
    type: 'objective',
    title: 'Objectif principal',
    description: 'La description de l\'objectif et sa vision',
    icon: Target,
    color: 'blue',
    getContent: (data) => {
      if (!data) return '';
      const title = typeof data.title === 'string' ? data.title : '';
      const description = typeof data.description === 'string' ? data.description : '';
      return title + (description ? `\n\n${description}` : '');
    },
    getPreview: (data) => data?.title || 'Aucun titre'
  },
  {
    type: 'recommendations',
    title: 'Recommandations',
    description: 'Les conseils et recommandations personnalisés',
    icon: Lightbulb,
    color: 'purple',
    getContent: (data) => {
      const rec = data?.recommendation;
      if (!rec || typeof rec !== 'string') return '';
      return formatRecommendations(rec);
    },
    getPreview: (data) => {
      const rec = data?.recommendation;
      if (!rec || typeof rec !== 'string') return 'Aucune recommandation';
      const cleaned = cleanTextForPreview(rec);
      return cleaned.length > 100 ? cleaned.substring(0, 100) + '...' : cleaned;
    }
  },
  {
    type: 'concepts',
    title: 'Concepts clés',
    description: 'Les concepts et notions importantes',
    icon: BookOpen,
    color: 'green',
    getContent: (data) => {
      const cours = data?.cours;
      if (!cours || typeof cours !== 'string') return '';
      return formatConcepts(cours);
    },
    getPreview: (data) => {
      const cours = data?.cours;
      if (!cours || typeof cours !== 'string') return 'Aucun concept';
      const cleaned = cleanTextForPreview(cours);
      return cleaned.length > 100 ? cleaned.substring(0, 100) + '...' : cleaned;
    }
  },
  {
    type: 'synthesis',
    title: 'Synthèse de recherche',
    description: 'La synthèse des sources analysées',
    icon: FileText,
    color: 'orange',
    getContent: (data) => {
      const synthesis = data?.synthesis;
      if (!synthesis || typeof synthesis !== 'string') return '';
      return formatSynthesis(synthesis);
    },
    getPreview: (data) => {
      const synthesis = data?.synthesis;
      if (!synthesis || typeof synthesis !== 'string') return 'Aucune synthèse';
      const cleaned = cleanTextForPreview(synthesis);
      return cleaned.length > 100 ? cleaned.substring(0, 100) + '...' : cleaned;
    }
  },
  {
    type: 'actionPlan',
    title: 'Plan d\'action',
    description: 'Les étapes concrètes à suivre',
    icon: List,
    color: 'blue',
    getContent: (data) => {
      const actionPlan = data?.actionPlan;
      if (!actionPlan) return '';
      return formatActionPlan(actionPlan);
    },
    getPreview: (data) => {
      const actionPlan = data?.actionPlan;
      if (!actionPlan) return 'Aucun plan d\'action';
      
      if (typeof actionPlan === 'string') {
        try {
          const parsed = JSON.parse(actionPlan);
          return `Plan structuré avec ${parsed.steps?.length || 0} étape(s)`;
        } catch (e) {
          return actionPlan.length > 100 ? actionPlan.substring(0, 100) + '...' : actionPlan;
        }
      } else if (typeof actionPlan === 'object' && actionPlan.steps) {
        return `Plan structuré avec ${actionPlan.steps.length} étape(s)`;
      }
      
      return 'Plan d\'action disponible';
    }
  },
  {
    type: 'sources',
    title: 'Sources sélectionnées',
    description: 'Les ressources et références utilisées',
    icon: Globe,
    color: 'green',
    getContent: (data) => {
      const sources = data?.sources;
      if (Array.isArray(sources) && sources.length > 0) {
        return sources.map(source => {
          const title = source?.title || source?.domain || 'Source sans titre';
          const url = source?.url || '';
          const description = source?.description || '';
          return `${title}${url ? ` - ${url}` : ''}${description ? ` : ${description}` : ''}`;
        }).join('\n');
      }
      if (typeof sources === 'string') return sources;
      return '';
    },
    getPreview: (data) => {
      const sources = data?.sources;
      if (Array.isArray(sources)) return `${sources.length} source(s) disponible(s)`;
      if (typeof sources === 'string' && sources.trim()) return 'Sources disponibles';
      return 'Aucune source';
    }
  }
];

// =============================================
// COMPOSANT PRINCIPAL
// =============================================

const ShareCompetence = ({ userId }) => {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  
  const [objectiveData, setObjectiveData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [shareError, setShareError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showElements, setShowElements] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  const { generateMessage, loading: messageLoading, error: messageError } = useAIMessage();
  
  const [selectedContent, setSelectedContent] = useState([
    { type: 'objective', selected: true },
    { type: 'recommendations', selected: false },
    { type: 'concepts', selected: false },
    { type: 'synthesis', selected: false },
    { type: 'actionPlan', selected: false },
    { type: 'sources', selected: false }
  ]);

  useEffect(() => {
    if (competenceId && userId) {
      loadObjectiveData();
    }
  }, [competenceId, userId]);

  useEffect(() => {
    if (objectiveData && selectedContent.some(item => item.selected)) {
      regenerateMessage();
    }
  }, [selectedContent, objectiveData]);

  const loadObjectiveData = async () => {
    if (!userId || !competenceId) {
      setError('Données manquantes pour le chargement');
      return;
    }

    setLoadingData(true);
    setError('');
    
    try {
      const data = await fetchObjectiveData(userId, competenceId);
      setObjectiveData(data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError(`Erreur lors du chargement: ${error.message}`);
    } finally {
      setLoadingData(false);
    }
  };

  const regenerateMessage = async () => {
    if (!objectiveData) return;
    
    try {
      const message = await generateMessage(
        { title: objectiveData.title, theme: objectiveData.theme },
        selectedContent
      );
      if (message) {
        setCustomMessage(message);
      }
    } catch (error) {
      console.error('Erreur régénération:', error);
    }
  };

  const toggleContentSelection = (type) => {
    setSelectedContent(prev => 
      prev.map(item => 
        item.type === type 
          ? { ...item, selected: !item.selected }
          : item
      )
    );
  };

  const handleEmailSubmit = async () => {
    if (selectedEmails.length === 0) {
      setShareError('Veuillez sélectionner au moins un destinataire.');
      return;
    }

    if (!objectiveData) {
      setShareError('Aucune donnée disponible pour le partage.');
      return;
    }

    setEmailLoading(true);
    setShareError('');

    try {
      const selectedItems = selectedContent.filter(item => item.selected);
      const htmlContent = generateExperienceShareHTML(customMessage, selectedItems, contentTypes, objectiveData);

      for (const email of selectedEmails) {
        await sendEmail(
          email, 
          `✨ ${objectiveData.title} - Partage d'expérience`,
          htmlContent,
          true
        );
      }
      
      setFeedbackMessage(`Email envoyé avec succès à ${selectedEmails.length} destinataire(s) !`);
      setSelectedEmails([]);
      
      setTimeout(() => {
        setFeedbackMessage('');
      }, 5000);
      
    } catch (error) {
      setShareError('Erreur lors de l\'envoi de l\'email.');
      console.error('Erreur email:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  if (loadingData && !objectiveData) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-lg text-gray-600">Chargement de l'objectif...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 lg:p-8 text-center">
            <AlertTriangle className="h-10 w-10 lg:h-12 lg:w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg lg:text-xl font-semibold text-red-800 mb-4">Erreur de chargement</h3>
            <p className="text-red-600 mb-6 text-sm lg:text-base">{error}</p>
            <button
              onClick={() => navigate('/competencesList')}
              className="px-4 lg:px-6 py-2 lg:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm lg:text-base"
            >
              Retour aux objectifs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="w-full bg-white border-b border-gray-200 px-4 lg:px-6 py-3 mb-4 lg:mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center text-xs lg:text-sm">
            <Link to="/competencesList" className="text-gray-600 hover:text-indigo-600 transition-colors">
              Mes objectifs
            </Link>
            <ChevronRight className="h-3 lg:h-4 w-3 lg:w-4 mx-1 text-gray-400" />
            <span className="text-indigo-600 font-medium">Partage d'objectif</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
        {feedbackMessage && (
          <div className="mb-4 lg:mb-6 bg-green-50 border border-green-200 text-green-700 p-3 lg:p-4 rounded-lg flex items-center text-sm lg:text-base">
            <Check className="w-4 h-4 lg:w-5 lg:h-5 mr-2 flex-shrink-0" />
            <p>{feedbackMessage}</p>
          </div>
        )}

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6 mb-6 lg:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
            <div className="flex-grow">
              <div className="flex flex-wrap items-center gap-2 lg:gap-3 mb-3">
                <h1 className="text-xl lg:text-2xl font-bold text-gray-800">
                  Partager mon objectif
                </h1>
                
                {objectiveData?.type && (
                  <span className={`px-2 lg:px-3 py-1 rounded-full text-xs lg:text-sm font-medium ${
                    objectiveData.type === 'project' ? 'bg-purple-50 text-purple-700' :
                    objectiveData.type === 'idea' ? 'bg-blue-50 text-blue-700' :
                    objectiveData.type === 'desire' ? 'bg-indigo-50 text-indigo-700' :
                    'bg-green-50 text-green-700'
                  }`}>
                    {objectiveData.type === 'project' ? 'Projet' :
                     objectiveData.type === 'idea' ? 'Idée' :
                     objectiveData.type === 'desire' ? 'Désir' :
                     objectiveData.type === 'skill' ? 'Compétence' : 'Objectif'}
                  </span>
                )}
                
                {objectiveData?.theme && (
                  <span className="px-2 lg:px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs lg:text-sm">
                    {objectiveData.theme}
                  </span>
                )}
              </div>
              
              <h2 className="text-lg lg:text-xl font-semibold text-gray-700 mb-2 break-words">
                {objectiveData?.title || 'Chargement...'}
              </h2>
              {objectiveData?.description && (
                <p className="text-sm lg:text-base text-gray-600 mt-1">
                  {objectiveData.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${showElements ? 'xl:grid-cols-2' : ''} gap-6 lg:gap-8`}>
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-800">
                  Message complet
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    <span className="hidden sm:inline">{showPreview ? 'Éditer le message' : 'Aperçu complet'}</span>
                    <span className="sm:hidden">{showPreview ? 'Éditer' : 'Aperçu'}</span>
                  </button>
                  <button
                    onClick={regenerateMessage}
                    disabled={messageLoading}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    {messageLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">Régénérer</span>
                  </button>
                </div>
              </div>
              
              {messageLoading ? (
                <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Génération...</span>
                </div>
              ) : showPreview ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <strong>Aperçu du message complet</strong> - Voici le rendu final avec tous vos éléments
                    </div>
                  </div>
                  
                  <div className="relative border rounded-lg overflow-hidden bg-gray-50">
                    <div className="absolute top-2 right-2 z-10 bg-white/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-600">
                      Aperçu complet
                    </div>
                    <div 
                      className="p-2 lg:p-4 overflow-y-auto text-sm bg-white"
                      style={{ 
                        height: showElements ? 
                          (typeof window !== 'undefined' && window.innerWidth < 768 ? '50vh' : '60vh') : 
                          (typeof window !== 'undefined' && window.innerWidth < 768 ? '65vh' : '75vh'),
                        minHeight: '400px'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: generateExperienceShareHTML(
                          customMessage,
                          selectedContent.filter(item => item.selected),
                          contentTypes,
                          objectiveData
                        )
                      }}
                    />
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800/75 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <span>↕️</span>
                      <span className="hidden sm:inline">Faites défiler pour tout voir</span>
                      <span className="sm:hidden">Défilez</span>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button
                      onClick={() => setShowPreview(false)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <EyeOff className="w-4 h-4" />
                      Retour à l'édition
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg border border-amber-100">
                    ✏️ <strong>Message d'introduction</strong> - Ce texte sera intégré harmonieusement avec les éléments sélectionnés
                  </div>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="w-full p-3 lg:p-4 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm lg:text-base"
                    rows={showElements ? (typeof window !== 'undefined' && window.innerWidth < 768 ? 6 : 8) : (typeof window !== 'undefined' && window.innerWidth < 768 ? 8 : 12)}
                    placeholder="Rédigez votre message d'introduction. Il sera automatiquement intégré avec les éléments que vous avez sélectionnés pour créer un message complet et harmonieux..."
                  />
                  
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500">
                    <span>Caractères: {customMessage.length}</span>
                    <span>{selectedContent.filter(item => item.selected).length} élément(s) sélectionné(s)</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">
                Destinataires
              </h3>
              
              <EmailInput
                selectedEmails={selectedEmails}
                onEmailChange={setSelectedEmails}
              />

              <div className="mt-6">
                <button
                  onClick={handleEmailSubmit}
                  disabled={emailLoading || !customMessage || messageLoading || selectedEmails.length === 0}
                  className={`w-full py-3 lg:py-4 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm lg:text-base ${
                    emailLoading || !customMessage || messageLoading || selectedEmails.length === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  {emailLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span className="hidden sm:inline">Envoyer le partage d'expérience ({selectedEmails.length})</span>
                      <span className="sm:hidden">Envoyer ({selectedEmails.length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {showElements && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-2">
                  <h3 className="text-lg lg:text-xl font-semibold text-gray-800">
                    Éléments à partager
                  </h3>
                  <button
                    onClick={() => setShowElements(!showElements)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors self-start sm:self-auto"
                  >
                    <EyeOff className="h-4 w-4" />
                    <span>Masquer</span>
                  </button>
                </div>
                
                <div className="space-y-3 lg:space-y-4">
                  {contentTypes.map((contentType) => {
                    const selectedItem = selectedContent.find(item => item.type === contentType.type);
                    const rawContent = contentType.getContent(objectiveData);
                    const hasContent = rawContent && typeof rawContent === 'string' && rawContent.trim();
                    const preview = contentType.getPreview(objectiveData);
                    
                    return (
                      <SelectableContentItem
                        key={contentType.type}
                        type={contentType.type}
                        title={contentType.title}
                        description={contentType.description}
                        icon={contentType.icon}
                        color={contentType.color}
                        selected={selectedItem?.selected || false}
                        onToggle={toggleContentSelection}
                        hasContent={!!hasContent}
                        preview={preview}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 lg:p-6 border border-gray-100">
                <h4 className="font-semibold text-gray-800 mb-3 text-sm lg:text-base">Résumé du partage</h4>
                <div className="text-xs lg:text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Format :</span>
                    <span className="font-medium">Message intégré</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Éléments :</span>
                    <span className="font-medium">{selectedContent.filter(item => item.selected).length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Destinataires :</span>
                    <span className="font-medium">{selectedEmails.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Caractères :</span>
                    <span className="font-medium">{customMessage.length}</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 lg:p-6">
                <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2 text-sm lg:text-base">
                  <Info className="h-4 lg:h-5 w-4 lg:w-5" />
                  Conseils pour un partage réussi
                </h4>
                <ul className="text-xs lg:text-sm text-yellow-700 space-y-2">
                  <li>• Message intégré et harmonieux combinant votre introduction et les éléments</li>
                  <li>• Format épuré sans en-tête superflu, centré sur le contenu</li>
                  <li>• Utilisez l'aperçu complet pour voir le rendu final</li>
                  <li>• Masquez cette section pour vous concentrer sur le message</li>
                  <li>• Le message se régénère automatiquement selon vos sélections</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {!showElements && (
          <div className="mt-6 lg:mt-8 text-center">
            <button
              onClick={() => setShowElements(true)}
              className="inline-flex items-center gap-2 px-4 lg:px-6 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm text-sm lg:text-base"
            >
              <Eye className="h-4 lg:h-5 w-4 lg:w-5 text-gray-600" />
              <span className="text-gray-700 font-medium">Afficher les éléments à partager</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {selectedContent.filter(item => item.selected).length}
              </span>
            </button>
          </div>
        )}

        {(shareError || messageError) && (
          <div className="mt-6 bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-600 text-sm lg:text-base">
              {shareError || messageError}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareCompetence;